import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../lib/translations';
import Section from '../components/UI/Section';
import Button from '../components/UI/Button';
import { motion as Motion } from 'framer-motion';
import { ArrowRight, Phone, Mail, ChevronLeft, ChevronRight, MapPin, Check, Award, Sparkles, Smile, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

const Home = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, testimonialsRes] = await Promise.all([
          fetch(`https://api.dev.auxo.dk/public/content/services`),
          fetch(`https://api.dev.auxo.dk/public/content/testimonials`),
        ]);
        
        const servicesData = await servicesRes.json();
        const testimonialsData = await testimonialsRes.json();

        setServices(servicesData);
        setTestimonials(testimonialsData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/front-side-ny.jpeg"
            alt="Kay Elizabeth Worsfold"
            className="w-full h-full object-cover scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/40 to-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-[745px]"
          >
            <h1 className="mb-8 tracking-tight">
              <span className="block text-3xl md:text-4xl lg:text-5xl font-serif font-light text-stone-500 mb-3">
                {t.hero.role}
              </span>
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif text-stone-900 leading-[1.1]">
                {t.hero.name}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-stone-600 mb-12 max-w-xl leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button to="/kontakt" variant="primary" className="px-8 py-4 text-lg">
                {lang === 'da' ? 'KONTAKT MIG' : 'CONTACT ME'}
              </Button>
              <Button to="/om-mig" variant="outline" className="px-8 py-4 text-lg border-taupe-300 text-taupe-700 hover:border-taupe-500 hover:text-taupe-900">
                {lang === 'da' ? 'MIN BAGGRUND' : 'MY FULL BACKGROUND'}
              </Button>
            </div>
          </Motion.div>
        </div>


        {/* Scroll Indicator */}
        <Motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            opacity: { duration: 1 },
            y: { duration: 1, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }
          }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-white font-medium">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/60 to-transparent"></div>
        </Motion.div>
      </section>

      {/* Combined Welcome & Experience Section */}
      <Section className="bg-gradient-to-br from-taupe-50 via-white to-taupe-100/30">
        {/* Welcome Part */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch mb-24">
          {/* Left Column: Image */}
          <div className="lg:col-span-4 flex flex-col h-full max-w-[380px] mx-auto lg:mx-0">
            {/* Spacer to align with the text container top (below "Velkommen" which is 16px + mb-4 = 32px) */}
            <div className="hidden lg:block h-[32px] shrink-0"></div>
            
            {/* The image container should grow to fill the remaining space above the buttons */}
            <div className="flex-1 min-h-[300px] sm:min-h-[400px] lg:min-h-0 relative w-full">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_13.png"
                alt="Kay Elizabeth Worsfold"
                className="w-full h-auto object-contain rounded-2xl"
              />
            </div>
            
            {/* Spacer to align with the buttons container (matching button height of ~52px) */}
            <div className="hidden lg:block h-[52px] shrink-0"></div>
          </div>

          {/* Right Column: Text and Buttons */}
          <div className="lg:col-span-8 flex flex-col justify-between h-full">
            <div>
              <span className="inline-block text-taupe-600 font-medium tracking-[0.2em] uppercase text-xs mb-4 h-[16px]">
                {lang === 'da' ? 'Velkommen' : 'Welcome'}
              </span>
              
              {/* Text Container */}
              <div className="space-y-6">
              {lang === 'da' ? (
                <>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif text-stone-900 leading-tight">
                    Hos mig tilbydes du et trygt og professionelt rum, hvor jeg hjælper dig med:
                  </h2>
                  <ul className="space-y-3 text-stone-600 leading-relaxed text-lg list-none pl-0">
                    <li className="flex items-start gap-3">
                      <span className="text-taupe-500 mt-1.5 text-sm">•</span>
                      <span>at få skabt klarhed og overblik over din situation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-taupe-500 mt-1.5 text-sm">•</span>
                      <span>praktiske værktøjer til at forstå og håndtere dine følelser</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-taupe-500 mt-1.5 text-sm">•</span>
                      <span>støtte til at opbygge indre styrke og større selvomsorg</span>
                    </li>
                  </ul>
                  <p className="text-stone-600 leading-relaxed text-lg m-0">
                    Jeg er autoriseret klinisk psykolog med over 20 års erfaring med at tilbyde terapi og coaching på dansk og engelsk. Jeg har specialiseret mig i Compassionfokuseret Terapi (CFT) som er en evidensbaseret og integrativ tilgang, der omfavner elementer fra kognitiv adfærdsterapi, mindfulness og psykodynamisk terapi.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif text-stone-900 leading-tight">
                    {t.welcome.title}
                  </h2>
                  <div className="space-y-6 text-stone-600 leading-relaxed text-lg">
                    {t.welcome.description1 && <p className="m-0">{t.welcome.description1}</p>}
                    {t.welcome.description2 && <p className="m-0">{t.welcome.description2}</p>}
                    {t.welcome.description3 && <p className="m-0">{t.welcome.description3}</p>}
                  </div>
                </>
              )}
            </div>
          </div>

            {/* Buttons Container */}
            <div className="mt-10 flex flex-wrap gap-4">
              {lang === 'da' ? (
                <>
                  <Button to="/om-mig" variant="outline">
                    MIN BAGGRUND
                  </Button>
                  <Button to="/services/compassion-fokuseret-terapi" variant="primary">
                    LÆS MERE OM CFT
                  </Button>
                </>
              ) : (
                <>
                  <Button to="/om-mig" variant="outline">
                    MY FULL BACKGROUND
                  </Button>
                  <Button to="/services/living-abroad" variant="primary">
                    LIVING ABROAD
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Experience & Offerings Part */}
        <div className="mt-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Experience Card */}
            <div className="lg:col-span-5 bg-taupe-100/50 p-10 md:p-12 rounded-3xl shadow-sm border border-taupe-200/40 flex flex-col justify-between relative overflow-hidden group hover:shadow-md transition-all duration-500">
              <div className="absolute top-0 right-0 w-32 h-32 bg-taupe-200/30 rounded-full blur-2xl -mr-10 -mt-10 transition-transform duration-700 group-hover:scale-110"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-8">
                  <span className="text-xs uppercase tracking-widest text-taupe-600 font-semibold">
                    {lang === 'da' ? 'Baggrund & Erfaring' : 'Background & Experience'}
                  </span>
                </div>

                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-5xl md:text-6xl font-serif text-stone-900 leading-none">20+</span>
                  <span className="text-stone-500 text-sm font-medium tracking-wide uppercase">
                    {lang === 'da' ? 'Års erfaring' : 'Years of experience'}
                  </span>
                </div>

                <p className="text-stone-700 leading-relaxed text-lg mb-8 whitespace-pre-line">
                  {t.treatment.experience}
                </p>
              </div>
            </div>

            {/* Offerings & Focus Areas Card */}
            <div className="lg:col-span-7 bg-white p-10 md:p-12 rounded-3xl shadow-sm border border-taupe-100/60 flex flex-col justify-between hover:shadow-md transition-all duration-500">
              <div>
                <div className="flex items-center gap-4 mb-8">
                  <span className="text-xs uppercase tracking-widest text-stone-400 font-medium">
                    {lang === 'da' ? 'JEG HJÆLPER DIG MED …' : 'What I can help you with'}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 md:gap-y-6">
                  {(lang === 'da' ? [
                    "At øge trivsel og glæde i hverdagen og arbejdslivet",
                    "At skrue ned for selvkritik & genvinde selvtillid og mod på livet",
                    "At håndtere angst, stress & udbrændthed",
                    "At håndtere ensomhed og stress som omsorgsgiver for en syg partner eller familiemedlem",
                    "At forstå og håndtere udfordrende dynamikker og kommunikationer i dine relationer",
                    "At bearbejde krise, sorg og traume",
                    "At leve med kronisk sygdom"
                  ] : [
                    "Overcoming self-criticism & developing greater self-confidence and resilience",
                    "Managing anxiety, stress and burnout",
                    "Finding emotional balance and reconnecting with your inner resources, drive and identity",
                    "Managing the transition from one culture to another, developing healthy and supportive connections",
                    "Healing from painful relationship experiences and patterns",
                    "Coping with career pressure and uncertainty at a time of transition",
                    "Coping with chronic illness, grief and loss",
                    "Recovering from trauma"
                  ]).map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-6 h-6 rounded-full bg-taupe-50 flex items-center justify-center text-taupe-500 shrink-0 mt-0.5">
                        <Check className="w-4 h-4" />
                      </div>
                      <span className="text-stone-600 text-lg leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>


      {/* Dedicated Compassion Specialization Section */}
      <Section className="bg-white border-t border-stone-100" spacing="py-6 md:py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-7 flex flex-col justify-between">
            <div>
              <span className="inline-flex items-center gap-2 text-taupe-600 font-medium tracking-[0.2em] uppercase text-xs mb-3">
                {t.compassion_focus.tag}
              </span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif text-stone-900 mb-4 leading-tight">
                {t.compassion_focus.title}
              </h2>
              <p className="text-base md:text-lg text-stone-700 font-medium mb-4 leading-relaxed">
                {t.compassion_focus.subtitle}
              </p>
              <div className="space-y-4 text-stone-600 leading-relaxed text-lg mb-6">
                <p>{t.compassion_focus.description1}</p>
                {t.compassion_focus.description2 && <p>{t.compassion_focus.description2}</p>}
              </div>

              {lang === 'da' && (
                <div className="flex flex-wrap gap-4 pt-2">
                  <Button 
                    to="/services/compassion-fokuseret-terapi" 
                    variant="primary"
                    className="px-6 py-3 text-sm inline-flex items-center"
                  >
                    <span>{t.compassion_focus.cta}</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
            {lang !== 'da' && (
              <div className="flex flex-wrap gap-4 pt-2">
                <Button 
                  to="/services/compassion-focused-therapy" 
                  variant="primary"
                  className="px-6 py-3 text-sm inline-flex items-center"
                >
                  <span>{t.compassion_focus.cta}</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>

          <div className="lg:col-span-5 flex flex-col justify-between">
            <div className="space-y-4 mb-6 lg:mb-0">
              {t.compassion_focus.benefits.map((benefit, idx) => (
                <div 
                  key={idx} 
                  className="bg-taupe-100/50 p-5 rounded-xl border border-taupe-200/40 hover:bg-taupe-200/30 transition-all duration-300"
                >
                  <h3 className="text-lg md:text-xl font-serif text-stone-900 mb-2 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-taupe-500 shadow-sm shrink-0">
                      {idx === 0 && <Smile className="w-4 h-4" />}
                      {idx === 1 && <Sparkles className="w-4 h-4" />}
                      {idx === 2 && <Heart className="w-4 h-4" />}
                    </div>
                    {benefit.title}
                  </h3>
                  <p className="text-stone-600 text-base leading-relaxed pl-11">
                    {benefit.desc}
                  </p>
                </div>
              ))}
            </div>

            {lang !== 'da' && (
              <div className="pt-2">
                <Button 
                  to="/services/living-abroad" 
                  variant="outline"
                  className="w-full sm:w-auto px-6 py-3 text-sm justify-center border-taupe-300 text-taupe-700 hover:border-taupe-500 hover:text-taupe-900 hover:bg-taupe-50/50"
                >
                  <span>LIVING ABROAD</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </Section>
      {/* Focus Areas (Services) - Alternating Grid */}
      <section className="bg-[#f9f9f8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900">{t.services.title}</h2>
        </div>
        
        <div className="grid grid-cols-1">
          {services
            .filter(service => {
              const isLivingAbroad = service.slug.en === 'living-abroad' || service.slug.da === 'udlandsophold';
              if (isLivingAbroad) return false;
              return true;
            })
            .map((service, index) => {
              const isTherapy = service.slug.en === 'therapy' || service.slug.da === 'terapi';
              const isCoaching = service.slug.en === 'coaching' || service.slug.da === 'coaching';
              const isSupervision = service.slug.en === 'supervision' || service.slug.da === 'supervision';
              const isCftBlock = service.slug.en === 'compassion-focused-therapy' || service.slug.da === 'compassion-fokuseret-terapi';
              
              const title = isCoaching 
                ? (lang === 'da' ? "Compassionfokuseret Terapi (CFT)" : "Compassion Focused Therapy (CFT)") 
                : isSupervision 
                  ? (lang === 'da' ? "Coaching" : "Coaching") 
                  : isCftBlock
                    ? (lang === 'da' ? "Supervision" : "Supervision")
                    : service.title[lang];

              const imageUrl = isCoaching
                ? "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/photo-compassion-focused-therapy-heading_1.jpg"
                : isSupervision
                  ? "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_14.png"
                  : isCftBlock
                    ? "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/93642205-53dd-434d-aaed-2330453fcaaa.jpg"
                    : service.image?.[0] || "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/e0fa67b5-501c-4ab8-8d65-ec8fb536b073.jpg";

              return (
                <div 
                  key={service.id} 
                  className="grid grid-cols-1 lg:grid-cols-2 items-stretch"
                >
                  <div className={`flex items-center lg:items-start justify-center bg-[#f9f9f8] p-4 md:p-6 lg:p-8 lg:pt-24 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="w-full overflow-hidden rounded-3xl shadow-sm border border-stone-100 max-w-[500px] lg:max-w-[600px] aspect-[1.2/1]">
                      <img 
                        src={imageUrl} 
                        alt={title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </div>
                  <div className={`bg-[#f9f9f8] flex flex-col items-center lg:justify-start justify-center p-12 md:p-20 lg:px-24 lg:py-24 text-center ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <h3 className="text-2xl md:text-3xl font-serif text-stone-900 mb-6">{title}</h3>
                    
                    {isCoaching ? (
                      <div className="text-stone-500 mb-10 md:max-w-none lg:max-w-md leading-relaxed space-y-4 text-center">
                        {lang === 'da' ? (
                          <>
                            <p>Compassionfokuseret Terapi (CFT) er en evidensbaseret psykologisk tilgang udviklet af professor Paul Gilbert. CFT integrerer viden fra tilknytningsteori, neurovidenskab og evolutionsteori.</p>
                            <p>Gennem CFT får du hjælp til at opnå bedre følelsesmæssig balance og dæmpe stress. Du får støtte til at mærke mere ro, fællesskab med andre og tro på dig selv.</p>
                            <p>CFT anvender veldokumenterede metoder fra flere effektive terapier, herunder metoder fra kognitiv adfærdsterapi og moderne psykodynamisk terapi samt mindfulness og visualiseringer til at tænde kroppens indre ro-system.</p>
                            <p>Jeg har gennem mange år specialiseret mig i CFT og Mindfulness-baseret kognitiv terapi (MBKT).</p>
                          </>
                        ) : (
                          <>
                            <p>Compassion Focused Therapy (CFT) is an evidence-based psychological approach developed by Professor Paul Gilbert. It integrates insights from attachment theory, neuroscience and evolutionary psychology.</p>
                            <p>CFT works by helping you to rebalance your emotions, reducing stress and supporting you to feel more calm, connected and confident.</p>
                            <p>CFT blends evidence-based tools from different therapies, including tools from Cognitive-behavioral therapy (CBT), Mindfulness, Psychodynamic therapies and physiological emotion regulation techniques.</p>
                            <p>I have specialist qualifications in CFT and Mindfulness-based Cognitive Therapy (MBCT).</p>
                          </>
                        )}
                      </div>
                    ) : isSupervision ? (
                      <div className="text-stone-500 mb-10 md:max-w-none lg:max-w-md leading-relaxed space-y-4 text-left">
                        {lang === 'da' ? (
                          <>
                            <p className="text-left font-medium text-stone-700">Coaching kan være det rette for dig, hvis du:</p>
                            <ul className="space-y-2 pl-4">
                              <li className="flex items-start">
                                <span className="text-taupe-500 mr-2 mt-1">•</span>
                                <span>Står foran et nyt kapitel i dit arbejds- eller privatliv</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-taupe-500 mr-2 mt-1">•</span>
                                <span>Har et ønske om et rum til at udforske og få indsigt i egne mønstre; det kan være selvtvivl, selvkritik, præstationspres eller Imposter Syndrome.</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-taupe-500 mr-2 mt-1">•</span>
                                <span>Du søger sparring omkring dine værdier og retning, professionelt eller privat</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-taupe-500 mr-2 mt-1">•</span>
                                <span>Ønsker et rum til at reflektere over din ledelse, trivsel og resultater</span>
                              </li>
                            </ul>
                            <p className="pt-2">Min tilgang til coaching kombinerer den psykologiske videnskab bag Compassionfokuseret Terapi med principper fra kognitiv adfærdsterapi og positiv psykologi. Vi har fokus på at skabe mere klarhed over de udfordringer, du står overfor her og nu, udvikling af nye perspektiver og støtte til at tage konkrete skridt i den retning, du gerne vil.</p>
                          </>
                        ) : (
                          <>
                            <p className="text-left font-medium text-stone-700">Coaching can be right for you, if you are:</p>
                            <ul className="space-y-2 pl-4">
                              <li className="flex items-start">
                                <span className="text-taupe-500 mr-2 mt-1">•</span>
                                <span>Navigating a personal or professional transition, including international relocation</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-taupe-500 mr-2 mt-1">•</span>
                                <span>Wanting to explore your own patterns of self-doubt, self-criticism or performance anxiety</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-taupe-500 mr-2 mt-1">•</span>
                                <span>Looking to re-align with your core values and sense of direction</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-taupe-500 mr-2 mt-1">•</span>
                                <span>Wanting to enhance your leadership and performance</span>
                              </li>
                            </ul>
                            <p className="pt-2">My approach to Coaching combines the psychological science behind Compassion Focused Therapy (CFT) with Positive Psychology and cognitive behavioural principles to help you find more clarity around the obstacles you are facing, develop new perspectives, and to support you in taking tangible steps towards meaningful change and growth.</p>
                            <p>Coaching is primarily present and future-focused and goal-oriented.</p>
                          </>
                        )}
                      </div>
                    ) : isCftBlock ? (
                      <div className="text-stone-500 mb-10 md:max-w-none lg:max-w-md leading-relaxed space-y-4 text-center">
                        {lang === 'da' ? (
                          <>
                            <p>Jeg tilbyder supervision på grundlag af Compassionfokuseret Terapi (CFT). CFT anvender veldokumenterede metoder fra flere effektive terapier, herunder metoder fra kognitiv adfærdsterapi (KAT) og Mindfulness-baseret kognitiv terapi (MBKT).</p>
                            <p>Jeg er godkendt supervisor i psykoterapi fra Dansk Psykolog Forening og Dansk Psykiatrisk Selskab</p>
                            <p>Jeg superviserer psykologer og læger, uanset om du er ny til CFT eller gerne vil bygge videre på og udvikle dine kompetencer i CFT.</p>
                          </>
                        ) : (
                          <>
                            <p>I offer clinical supervision using the framework of Compassion Focused Therapy (CFT).</p>
                            <p>I am an approved supervisor from the Danish Psychological Association and the Danish Psychiatric Society.</p>
                            <p>I offer supervision for psychologists and doctors who are either new to or looking to build and enhance skills in Compassion Focused Therapy.</p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div 
                        className="text-stone-500 mb-10 md:max-w-none lg:max-w-md leading-relaxed space-y-4"
                        dangerouslySetInnerHTML={{ 
                          __html: isTherapy
                            ? service.description[lang]
                            : (service.description[lang].split('</p>')[0] + '</p>')
                        }}
                      />
                    )}

                    {isCoaching ? (
                      <div className="flex flex-wrap gap-4 justify-center">
                        <Button 
                          to={lang === 'da' ? '/services/compassion-fokuseret-terapi' : '/services/compassion-focused-therapy'} 
                          variant="outline"
                        >
                          {lang === 'da' ? 'LÆS MERE OM CFT' : 'READ MORE ABOUT CFT'}
                        </Button>
                        <Button 
                          to="/om-mig" 
                          variant="outline"
                        >
                          {lang === 'da' ? 'MIN BAGGRUND' : 'MY FULL BACKGROUND'}
                        </Button>
                      </div>
                    ) : (isCftBlock || isSupervision) ? (
                      null
                    ) : (
                      <Button 
                        to={isTherapy 
                          ? (lang === 'da' ? '/services/compassion-fokuseret-terapi' : '/services/compassion-focused-therapy')
                          : `/services/${service.slug[lang]}`} 
                        variant="outline"
                      >
                        {isTherapy 
                          ? (lang === 'da' ? 'LÆS MERE OM CFT' : 'READ MORE ABOUT CFT')
                          : (lang === 'da' ? 'Læs mere' : 'Read more')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </section>
      {/* Contact Section */}
      <section className="bg-stone-100 py-12 md:py-16 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">
                {lang === 'da' ? 'Kontakt' : 'Contact'}
              </h2>
              
              <div className="space-y-8">
                {/* Email */}
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm">
                    <Mail className="w-5 h-5 text-taupe-400" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">
                      {lang === 'da' ? 'MAIL' : 'EMAIL'}
                    </p>
                    <a href="mailto:info@psykolog-kayworsfold.dk" className="text-xl text-stone-900 hover:text-taupe-500 transition-colors">
                      info@psykolog-kayworsfold.dk
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm">
                    <Phone className="w-5 h-5 text-taupe-400" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">
                      {lang === 'da' ? 'TELEFON' : 'PHONE'}
                    </p>
                    <a href="tel:+4530463052" className="text-xl text-stone-900 hover:text-taupe-500 transition-colors">
                      +45 3046 3052
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm">
                    <MapPin className="w-5 h-5 text-taupe-400" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">
                      {lang === 'da' ? 'ADRESSE' : 'ADDRESS'}
                    </p>
                    <p className="text-xl text-stone-900">
                      {lang === 'da' 
                        ? 'Store Kannikestræde 18, 2. th, 1169 København K' 
                        : 'Store Kannikestræde 18, 2. th, 1169 Copenhagen K'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Map */}
            <div className="w-full h-[350px] md:h-[450px] rounded-3xl overflow-hidden shadow-sm border border-stone-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2249.578631557922!2d12.57279137746404!3d55.68067969792043!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4652531639f75d61%3A0x86705f939e6e0018!2sStore%20Kannikestr%C3%A6de%2018%2C%201169%20K%C3%B8benhavn!5e0!3m2!1sda!2sdk!4v1718700000000!5m2!1sda!2sdk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={lang === 'da' ? 'Kort over Store Kannikestræde 18' : 'Map of Store Kannikestræde 18'}
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
